import { MeilisearchBaseHit } from '@abovo/shared-models';
import { GROUPING_TOUR_ID, TourRunnerService, useSnackbar } from '@abovo/shared-services';
import { computed, DOCUMENT, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { IStepOption } from 'ngx-ui-tour-md-menu';
import { filter, firstValueFrom, of, timeout } from 'rxjs';
import { MAX_GROUPING_DEPTH } from '../constants/filter-preset.constants';
import { FilterStateService } from '../services/filter-state/filter-state.service';
import { GROUPING_TOUR_ANCHOR, GROUPING_TOUR_ROUTE, GROUPING_TOUR_STEP } from './grouping-tour.constants';

/** What the filterbar settings menu needs from the tour to render its anchors and suppress hover submenus. */
export interface GroupingTourBinding {
   /** True while the tour runs; the date facets drop their hover submenu so it can't cover the popover. */
   running: Signal<boolean>;

   /** Each groupable row binds `demoAnchorPrefix + facet` as its anchor id (see {@link GROUPING_TOUR_ANCHOR}). */
   demoAnchorPrefix: string;
}

/** Time Material needs to close the menu chain, before the next step's popover is placed behind it. */
const MENU_TEARDOWN_MS = 450;

/** Gap between opening the settings menu and its grouping submenu, so the second trigger is there to click. */
const MENU_OPEN_STAGGER_MS = 150;

/** How long a filterbar gets to appear and report its groupable facets after the tour navigates to one. */
const FILTERBAR_WAIT_MS = 10000;

/**
 * Drives the guided tour of the filterbar's grouping feature. It provides the tour-specific parts — the
 * steps and the auto-start policy — while the generic {@link TourRunnerService} handles the shared
 * mechanics (step defaults, lifecycle wiring, and the manual/automatic triggers).
 *
 * The second step is a live demo: clicking a real facet row applies the grouping. Any click in that menu
 * closes the menu chain and so detaches the step's own anchor, which is why the tour has to leave the step
 * within the same click — `nextOnAnchorClick` for the row the step points at, {@link advanceOnMenuClick}
 * for a click anywhere else in the menu — and why the stacking step waits out the teardown before it is placed.
 */
@Injectable({ providedIn: 'root' })
export class GroupingTourService {
   private readonly tourRunner = inject(TourRunnerService);
   private readonly translate = inject(TranslateService);
   private readonly document = inject(DOCUMENT);
   private readonly router = inject(Router);
   private readonly snackbar = useSnackbar();

   /** Whether a filterbar offering grouping is currently on screen. Pushed by the settings component. */
   private readonly available = signal(false);

   /** The facet the demo step anchors to (the first that groups directly), or null when there is none. */
   private readonly demoGroup = signal<string | null>(null);

   private readonly runningState = signal(false);

   /** Teardown for the demo step's menu listener, or null while there is none (see {@link advanceOnMenuClick}). */
   private stopListeningForMenuClick: (() => void) | null = null;

   readonly running = this.runningState.asReadonly();

   /** Emits once a filterbar offering grouping is on screen — the tour waits on this after navigating. */
   private readonly filterbarAvailable$ = toObservable(this.available).pipe(filter(Boolean));

   private readonly registration = this.tourRunner.register({
      tourId: GROUPING_TOUR_ID,
      start: () => this.start(),
      autoStart: {
         canStart: this.available,
         notBefore: null,
      },
   });

   /**
    * Binds a filterbar's grouping state to the tour, and hands back what that filterbar's template needs.
    * Call it from a field initializer of the settings component: the effect below follows that component's
    * lifetime, so the filterbar carries no tour logic of its own beyond the anchors in its template.
    */
   connect(filtersState: FilterStateService<MeilisearchBaseHit>): GroupingTourBinding {
      const groupableFields = filtersState.groupableFields;

      /** The demo step can only anchor to a facet that groups directly — date facets open a submenu. */
      const demoTarget = computed(() => groupableFields().find((group) => !group.units)?.field ?? null);

      // A narrow filter can take the demo row away without grouping itself going: that drops one step, not the tour.
      effect((onCleanup) => {
         this.available.set(groupableFields().length > 0);
         this.demoGroup.set(demoTarget());
         onCleanup(() => {
            this.available.set(false);
            this.demoGroup.set(null);
         });
      });

      return { running: this.running, demoAnchorPrefix: GROUPING_TOUR_ANCHOR.demoRowPrefix };
   }

   /** Starts (or restarts) the tour from the beginning, going to a page with a filterbar first if needed. */
   start(): void {
      if (this.available()) {
         this.run();
         return;
      }
      void this.startAfterNavigation();
   }

   /**
    * The tour is offered from anywhere (changelog notification, shortcut panel), so a start without a
    * filterbar on screen takes the user to one first and waits for it to report its groupable facets —
    * which only happens once that page's first results are in. The snackbar is the last resort for a page
    * that never gets there (no access, a failed search).
    */
   private async startAfterNavigation(): Promise<void> {
      const navigated = await this.router.navigate([GROUPING_TOUR_ROUTE]);
      const available = navigated && (await firstValueFrom(this.filterbarAvailable$.pipe(timeout({ first: FILTERBAR_WAIT_MS, with: () => of(false) }))));

      if (!available) {
         this.snackbar.show(this.translate.instant('filterbar.tour.unavailable'));
         return;
      }
      this.run();
   }

   /** Runs the tour against the filterbar that is on screen right now. */
   private run(): void {
      this.runningState.set(true);

      // The demo step is dropped when no facet can host it, rather than shown against a missing anchor.
      const demoGroup = this.demoGroup();
      const demoAnchorId = demoGroup === null ? null : GROUPING_TOUR_ANCHOR.demoRowPrefix + demoGroup;

      this.tourRunner.run({
         steps: this.buildSteps(demoAnchorId),
         // Grouping and filter changes are pushed to the URL query params, and ngx ends a tour on any
         // navigation unless this is set — which killed the tour on its own demo click. Leaving the page
         // still ends it: that destroys the anchors, and with them the popover.
         stepDefaults: { allowUserInitiatedNavigation: true },
         onStepShow: (step) => {
            if (step.stepId === GROUPING_TOUR_STEP.chooseGrouping && demoAnchorId !== null) {
               this.advanceOnMenuClick(demoAnchorId);
            }
         },
         onStepHideForwards: (step) => {
            if (step.stepId === GROUPING_TOUR_STEP.settings && demoAnchorId !== null) {
               this.openGroupingMenus();
            }
         },
         onEnd: () => {
            this.stopListeningForMenuClick?.();
            this.runningState.set(false);
         },
      });
   }

   /**
    * Opens the settings menu and its grouping submenu on the user's behalf, so the demo step's anchor exists
    * and is laid out before that step shows. The user is not asked to do it: "Groepering" is a hover-opening
    * submenu trigger, and a popover hanging off the element the pointer must reach locks up the page.
    */
   private openGroupingMenus(): void {
      this.clickAnchor(GROUPING_TOUR_ANCHOR.settings);
      setTimeout(() => this.clickAnchor(GROUPING_TOUR_ANCHOR.groupingItem), MENU_OPEN_STAGGER_MS);
   }

   /** Clicks a tour anchor by id, so no menu wiring leaks into the filterbar. */
   private clickAnchor(anchorId: string): void {
      const anchor = this.findAnchor(anchorId);
      if (!anchor || anchor.getAttribute('aria-expanded') === 'true') {
         return;
      }
      anchor.click();
   }

   private findAnchor(anchorId: string): HTMLElement | null {
      return this.document.querySelector<HTMLElement>(`[tourAnchor="${anchorId}"]`);
   }

   /**
    * Leaves the demo step on a click anywhere in the grouping menu other than the row it points at
    * (`nextOnAnchorClick` covers that one). Material closes a menu on any click inside its panel, and the
    * popover going down with the panel reads to ngx as the user dismissing the tour — so the step has to be
    * left while the click is still being dispatched. Hence the capture-phase listener, which runs before
    * Material starts the teardown; the click itself is left alone and applies whatever the user picked.
    */
   private advanceOnMenuClick(demoAnchorId: string): void {
      this.stopListeningForMenuClick?.();

      const demoRow = this.findAnchor(demoAnchorId);
      const panel = demoRow?.closest<HTMLElement>('.mat-mdc-menu-panel') ?? null;
      if (!demoRow || !panel) {
         return;
      }

      const onPanelClick = (event: Event): void => {
         const target = event.target;
         const onDemoRow = target instanceof Node && demoRow.contains(target);
         if (onDemoRow || this.tourRunner.currentStepId() !== GROUPING_TOUR_STEP.chooseGrouping) {
            return;
         }
         this.stopListeningForMenuClick?.();
         this.tourRunner.next();
      };

      panel.addEventListener('click', onPanelClick, { capture: true });
      this.stopListeningForMenuClick = () => {
         panel.removeEventListener('click', onPanelClick, { capture: true });
         this.stopListeningForMenuClick = null;
      };
   }

   private buildSteps(demoAnchorId: string | null): IStepOption[] {
      const translate = (key: string, params?: Record<string, number>): string => this.translate.instant(`filterbar.tour.${key}`, params);

      const demoSteps: IStepOption[] =
         demoAnchorId === null
            ? []
            : [
                 {
                    stepId: GROUPING_TOUR_STEP.chooseGrouping,
                    anchorId: demoAnchorId,
                    title: translate('choose_grouping.title'),
                    content: translate('choose_grouping.content'),
                    placement: { horizontal: true, xPosition: 'before' },
                    // The click that groups also closes both menus, so the tour has to leave this step with it.
                    nextOnAnchorClick: true,
                    // Wait for the menus opened on the previous step's exit: a freshly created row still
                    // measures as a (0,0) rect. The async fallback only covers a menu that is slow or absent.
                    delayBeforeStepShow: 500,
                    isAsync: true,
                    isOptional: true,
                    asyncStepTimeout: 5000,
                 },
              ];

      return [
         {
            stepId: GROUPING_TOUR_STEP.settings,
            anchorId: GROUPING_TOUR_ANCHOR.settings,
            title: translate('settings.title'),
            content: translate('settings.content'),
            // The cog sits at the right of the filterbar, so the popover goes left of the viewport edge.
            placement: { horizontal: true, xPosition: 'before' },
            // Auto-start can fire in the same flush that renders the filterbar; wait for the anchor.
            isAsync: true,
            asyncStepTimeout: 3000,
         },
         ...demoSteps,
         {
            stepId: GROUPING_TOUR_STEP.stacking,
            anchorId: GROUPING_TOUR_ANCHOR.settings,
            title: translate('stacking.title'),
            content: translate('stacking.content', { max: MAX_GROUPING_DEPTH }),
            placement: { horizontal: true, xPosition: 'before' },
            // Reached by the click that closes the menus; placing the popover mid-teardown lands it on them.
            delayBeforeStepShow: MENU_TEARDOWN_MS,
            // Back would otherwise walk into the demo step, whose anchor died with the menu.
            prevStep: GROUPING_TOUR_STEP.settings,
         },
         {
            stepId: GROUPING_TOUR_STEP.filtering,
            anchorId: GROUPING_TOUR_ANCHOR.filters,
            title: translate('filtering.title'),
            content: translate('filtering.content'),
            placement: { yPosition: 'below' },
         },
         {
            stepId: GROUPING_TOUR_STEP.bookmark,
            anchorId: GROUPING_TOUR_ANCHOR.bookmark,
            title: translate('bookmark.title'),
            content: translate('bookmark.content'),
            placement: { yPosition: 'below' },
            // Pages that set a route `code` hide the bookmark star; skip the step rather than end there.
            isOptional: true,
         },
      ];
   }
}
